import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import type { SessionUser } from '@padhai/shared';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { PageLoading, RequireAuth, RequireRole } from './components/Guards.js';
import { BrandMark } from './components/Brand.js';
import { OfflineBanner } from './components/OfflineBanner.js';
import { InstallBanner } from './components/InstallBanner.js';
import { Button, cx } from './components/ui.js';
import { useAuth } from './lib/auth.js';
import { Login } from './pages/Login.js';
import { NotBuiltYet } from './pages/NotBuiltYet.js';

/*
 * Route pages are loaded on demand. The pilot runs on low-end Android over a
 * school's connection (section 3.3), and a student's first load should not
 * carry the teacher console, the admin pages or the syllabus catalog. Login
 * stays in the main bundle: it is the first screen for anyone signed out, and
 * splitting it would only add a round trip before the form appears.
 *
 * The pages use named exports, so each import maps the one it needs to the
 * default `lazy` expects.
 */
const AdminContent = lazy(() =>
  import('./pages/AdminContent.js').then((m) => ({ default: m.AdminContent })),
);
const AdminPeople = lazy(() =>
  import('./pages/AdminPeople.js').then((m) => ({ default: m.AdminPeople })),
);
const AdminSchools = lazy(() =>
  import('./pages/AdminSchools.js').then((m) => ({ default: m.AdminSchools })),
);
const AdminUsage = lazy(() =>
  import('./pages/AdminUsage.js').then((m) => ({ default: m.AdminUsage })),
);
const Catalog = lazy(() => import('./pages/Catalog.js').then((m) => ({ default: m.Catalog })));
const ChangeCredential = lazy(() =>
  import('./pages/ChangeCredential.js').then((m) => ({ default: m.ChangeCredential })),
);
const Learn = lazy(() => import('./pages/Learn.js').then((m) => ({ default: m.Learn })));
const ParentChapter = lazy(() =>
  import('./pages/ParentChapter.js').then((m) => ({ default: m.ParentChapter })),
);
const ParentSubject = lazy(() =>
  import('./pages/ParentSubject.js').then((m) => ({ default: m.ParentSubject })),
);
const ParentHome = lazy(() =>
  import('./pages/ParentHome.js').then((m) => ({ default: m.ParentHome })),
);
const PastPapers = lazy(() =>
  import('./pages/PastPapers.js').then((m) => ({ default: m.PastPapers })),
);
const Practice = lazy(() => import('./pages/Practice.js').then((m) => ({ default: m.Practice })));
const StudentPractice = lazy(() =>
  import('./pages/StudentPractice.js').then((m) => ({ default: m.StudentPractice })),
);
const MixedPractice = lazy(() =>
  import('./pages/StudentPractice.js').then((m) => ({ default: m.MixedPractice })),
);
const StudentPapers = lazy(() =>
  import('./pages/StudentPapers.js').then((m) => ({ default: m.StudentPapers })),
);
const Review = lazy(() => import('./pages/Review.js').then((m) => ({ default: m.Review })));
const StudentReport = lazy(() =>
  import('./pages/StudentReport.js').then((m) => ({ default: m.StudentReport })),
);
const StudentToday = lazy(() =>
  import('./pages/StudentToday.js').then((m) => ({ default: m.StudentToday })),
);
const TeacherClass = lazy(() =>
  import('./pages/TeacherClass.js').then((m) => ({ default: m.TeacherClass })),
);
const TopicProgress = lazy(() =>
  import('./pages/TopicProgress.js').then((m) => ({ default: m.TopicProgress })),
);
const TopicShell = lazy(() =>
  import('./pages/TopicShell.js').then((m) => ({ default: m.TopicShell })),
);

/**
 * App shell and routing.
 *
 * The nav changes with the role rather than showing everyone everything and
 * refusing on click; the server enforces the same rules either way.
 */
export function App() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Someone holding a credential another person chose sees nothing else until
  // they replace it. The API refuses every other route meanwhile, so this is
  // the honest screen rather than a page of errors.
  const held = user?.mustChangeCredential ?? false;
  const classQuery = useClassQuery();

  return (
    <div className="flex min-h-dvh flex-col">
      <OfflineBanner />
      <header
        className={cx(
          'sticky top-0 z-20 border-b border-line bg-surface/85 backdrop-blur',
          // The sign-in page carries the full logo; a second, small one above it is noise.
          !user && pathname === '/login' && 'hidden',
        )}
      >
        {/*
         * One row from 640px up. Below that the nav drops to a row of its own,
         * full width: on a 360px phone the wordmark, four tabs and the account
         * controls do not fit in one line, and squeezing them clipped the last
         * tab off the screen and wrapped "Sign out" onto two lines.
         */}
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 px-4 sm:flex-nowrap">
          <Link
            to={`/${classQuery}`}
            className="flex shrink-0 items-center py-2"
            aria-label="Padhai home"
          >
            <BrandMark />
          </Link>

          {user ? <SignedIn held={held} /> : null}

          {user && !held ? <MainNav user={user} /> : null}
        </div>
      </header>

      {/*
       * Clipped sideways so one page's row that is a pixel too wide — the
       * console's tab bar at 360px was — cannot make the whole app pan
       * sideways on a phone. `clip` rather than `hidden`, which would make
       * main a scroll container.
       */}
      <main
        className={cx(
          'aurora relative isolate mx-auto w-full max-w-6xl flex-1 overflow-x-clip px-4 py-6 sm:py-9',
          // Room for the phone tab bar, so it never covers the last thing on a page.
          user?.role === 'student' && !held && 'pb-28 sm:pb-9',
        )}
      >
        {/*
         * Inside the shell, so a page that fails (most often its code not
         * downloading offline) keeps the header and the offline bar, and reset
         * by the path so moving to another page clears it.
         */}
        {user && !held ? <InstallBanner /> : null}
        <ErrorBoundary resetKey={pathname}>
          <Suspense fallback={<PageLoading />}>
            {held ? <ChangeCredential forced /> : <AppRoutes />}
          </Suspense>
        </ErrorBoundary>
      </main>

      {user?.role === 'student' && !held ? <StudentTabBar /> : null}

      <footer
        className={cx(
          'border-t border-line px-4 py-4 text-center text-xs text-ink-faint',
          user?.role === 'student' && !held && 'hidden sm:block',
        )}
      >
        Padhai{user ? ` · ${boardLabel(user.board)}` : ''} · Smarter schools. Brighter students.
      </footer>
    </div>
  );
}

/** "AP SSC Class 10", from the school's board rather than a string in the shell. */
function boardLabel(board: SessionUser['board']): string {
  return `${board.code.replace(/_/g, ' ')} Class ${board.grade}`;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/*
       * A parent's pages. A parent is not a signed-in user here: their session
       * is a cookie of its own (migration 0014), checked by every parent route,
       * and the pages send a parent back to sign-in on a 401.
       */}
      <Route path="/parent" element={<ParentHome />} />
      <Route path="/parent/subjects/:subjectId" element={<ParentSubject />} />
      <Route path="/parent/chapters/:topicId" element={<ParentChapter />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <Home />
          </RequireAuth>
        }
      />

      <Route
        path="/account"
        element={
          <RequireAuth>
            <ChangeCredential />
          </RequireAuth>
        }
      />

      <Route
        path="/topics/:topicId"
        element={
          <RequireRole roles={['student']}>
            <TopicShell />
          </RequireRole>
        }
      >
        <Route index element={<Navigate to="learn" replace />} />
        <Route path="learn" element={<Learn />} />
        <Route path="papers" element={<PastPapers />} />
        <Route path="practice" element={<Practice />} />
        <Route path="progress" element={<TopicProgress />} />
      </Route>

      {/* A student's own pages, across the chapters they choose. */}
      <Route
        path="/practice"
        element={
          <RequireRole roles={['student']}>
            <StudentPractice />
          </RequireRole>
        }
      />
      <Route
        path="/practice/mix"
        element={
          <RequireRole roles={['student']}>
            <MixedPractice />
          </RequireRole>
        }
      />
      <Route
        path="/papers"
        element={
          <RequireRole roles={['student']}>
            <StudentPapers />
          </RequireRole>
        }
      />
      <Route
        path="/review"
        element={
          <RequireRole roles={['student']}>
            <Review />
          </RequireRole>
        }
      />

      <Route
        path="/usage"
        element={
          <RequireRole roles={['admin']}>
            <AdminUsage />
          </RequireRole>
        }
      />

      <Route
        path="/people"
        element={
          <RequireRole roles={['admin']}>
            <AdminPeople />
          </RequireRole>
        }
      />

      <Route
        path="/schools"
        element={
          <RequireRole roles={['admin']}>
            <AdminSchools />
          </RequireRole>
        }
      />

      <Route
        path="/content"
        element={
          <RequireRole roles={['admin']}>
            <AdminContent />
          </RequireRole>
        }
      />

      {/*
       * One student, for staff. The API additionally requires a teacher
       * share a class with them, so this guard is the coarse half of the
       * check and not the whole of it.
       */}
      <Route
        path="/students/:studentId"
        element={
          <RequireRole roles={['teacher', 'admin']}>
            <StudentReport />
          </RequireRole>
        }
      />

      <Route
        path="/catalog"
        element={
          <RequireRole roles={['teacher', 'admin']}>
            <Catalog />
          </RequireRole>
        }
      />

      <Route
        path="*"
        element={
          <NotBuiltYet
            title="Page not found"
            step=""
            items={['That page does not exist. Use the links at the top.']}
          />
        }
      />
    </Routes>
  );
}

/**
 * Routes to the right home screen for whoever is signed in.
 *
 * An admin has no class of their own, so sending them to the class console
 * would land them on an empty state. Cost and usage is their job (section 2).
 */
function Home() {
  const { user } = useAuth();
  if (user?.role === 'student') return <StudentToday />;
  if (user?.role === 'admin') return <AdminUsage />;
  return <TeacherClass />;
}

/**
 * `?class=<id>` for the class a teacher is looking at, or nothing. The console,
 * the syllabus and a student report all say which class in the URL, and the
 * shell's own links carry it on, so a teacher of two sections who opens a
 * 10-B report and then presses Class lands on 10-B, not on their first class.
 */
function useClassQuery(): string {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const requested = params.get('class');
  if (!user || user.role !== 'teacher' || !requested) return '';
  return user.classes.some((klass) => klass.id === requested)
    ? `?class=${encodeURIComponent(requested)}`
    : '';
}

function MainNav({ user }: { user: SessionUser }) {
  const classQuery = useClassQuery();
  // Staff tabs get a row of their own on a phone; a student's move to the
  // bottom bar there (StudentTabBar), so the top stays the logo and account.
  const ownRow = user.role !== 'student';
  return (
    <nav
      aria-label="Main"
      className={cx(
        'min-w-0',
        !ownRow && 'hidden sm:block',
        ownRow &&
          'order-last -mx-4 w-[calc(100%+2rem)] border-t border-line px-3 sm:order-none sm:mx-0 sm:w-auto sm:border-t-0 sm:px-0',
      )}
    >
      <ul className="flex items-center gap-1 overflow-x-auto py-1.5">
        {user.role === 'student' ? (
          <>
            <li>
              <TabLink to="/">Today</TabLink>
            </li>
            <li>
              <TabLink to="/practice" also={['/practice/']}>
                Practice
              </TabLink>
            </li>
            <li>
              <TabLink to="/papers">Past papers</TabLink>
            </li>
            <li>
              <TabLink to="/review">Review</TabLink>
            </li>
          </>
        ) : user.role === 'teacher' ? (
          <>
            <li>
              {/* A student's report is part of the class, not a page of its own. */}
              <TabLink to={`/${classQuery}`} also={['/students/']}>
                Class
              </TabLink>
            </li>
            <li>
              <TabLink to={`/catalog${classQuery}`}>Syllabus</TabLink>
            </li>
          </>
        ) : (
          <>
            <li>
              <TabLink to="/">Usage</TabLink>
            </li>
            <li>
              <TabLink to="/people" also={['/students/']}>
                People
              </TabLink>
            </li>
            <li>
              <TabLink to="/catalog">Syllabus</TabLink>
            </li>
            <li>
              <TabLink to="/content">Content</TabLink>
            </li>
            {/* Only for an admin named an operator on the server (migration 0010). */}
            {user.operator ? (
              <li>
                <TabLink to="/schools">Schools</TabLink>
              </li>
            ) : null}
          </>
        )}
      </ul>
    </nav>
  );
}

function SignedIn({ held }: { held: boolean }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  if (!user) return null;

  // The class the teacher is looking at, when the console says which in the
  // URL; otherwise their first. It used to always be the first, so a teacher
  // of two sections was told "10-A" while reading 10-B.
  const viewing = user.classes.find((klass) => klass.id === params.get('class')) ?? user.classes[0];
  const role = user.rollNo ? `Roll ${user.rollNo}` : user.role === 'admin' ? 'Admin' : 'Teacher';

  return (
    <div className="order-last ml-auto flex min-w-0 shrink-0 items-center gap-1 sm:gap-3">
      {held ? null : (
        <Link
          to="/account"
          title={user.role === 'student' ? 'Change your PIN' : 'Change your password'}
          className="flex min-w-0 items-center gap-3 rounded-control px-2 py-1 text-right text-sm leading-tight hover:bg-surface-sunken"
        >
          {/* Never the level next to a student's name (spec section 9). */}
          <span className="hidden min-w-0 sm:block">
            <span className="block max-w-[12rem] truncate font-medium">{user.name}</span>
            <span className="block truncate text-xs text-ink-faint">
              {role}
              {user.role !== 'admin' && viewing ? ` · ${viewing.name}` : ''}
            </span>
          </span>
          {/*
           * Said in words at every width. On a wide screen the link used to be
           * only a name, and nothing said that a name is where the PIN or the
           * password is changed.
           */}
          <span className="text-ink-soft sm:font-medium sm:text-brand-strong">Account</span>
        </Link>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="whitespace-nowrap"
        onClick={() => {
          void signOut().then(() => navigate('/login', { replace: true }));
        }}
      >
        Sign out
      </Button>
    </div>
  );
}

const STUDENT_TABS: Array<{ to: string; label: string; also?: string[]; icon: ReactNode }> = [
  {
    to: '/',
    label: 'Today',
    also: ['/topics/'],
    icon: <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4.5v-6h-5v6H5a1 1 0 0 1-1-1v-9.5Z" />,
  },
  {
    to: '/practice',
    label: 'Practice',
    also: ['/practice/'],
    icon: (
      <>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="12" cy="12" r="0.6" />
      </>
    ),
  },
  {
    to: '/papers',
    label: 'Papers',
    icon: (
      <>
        <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
        <path d="M14 3v4h4M9 12h6M9 16h6" />
      </>
    ),
  },
  {
    to: '/review',
    label: 'Review',
    icon: (
      <>
        <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z" />
        <path d="m9 10 2 2 4-4" />
      </>
    ),
  },
];

/**
 * A student's four places, at the bottom of a phone: Today, Practice, Past
 * papers and Review, each with an icon and its name. The top row scrolled
 * sideways on a narrow screen and sat where a thumb does not reach.
 */
function StudentTabBar() {
  const { pathname } = useLocation();
  return (
    <nav
      aria-label="Student"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur sm:hidden"
    >
      <ul className="grid grid-cols-4">
        {STUDENT_TABS.map((tab) => {
          const active =
            tab.to === '/'
              ? pathname === '/' || (tab.also ?? []).some((prefix) => pathname.startsWith(prefix))
              : pathname === tab.to ||
                (tab.also ?? []).some((prefix) => pathname.startsWith(prefix));
          return (
            <li key={tab.to}>
              <Link
                to={tab.to}
                aria-current={active ? 'page' : undefined}
                className={cx(
                  'flex min-h-14 flex-col items-center justify-center gap-0.5 text-[0.7rem] font-medium',
                  active ? 'text-brand-strong' : 'text-ink-soft',
                )}
              >
                <span
                  className={cx(
                    'flex h-7 w-12 items-center justify-center rounded-full transition-colors',
                    active && 'bg-brand-soft',
                  )}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="size-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {tab.icon}
                  </svg>
                </span>
                {tab.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function TabLink({
  to,
  also = [],
  children,
}: {
  to: string;
  /** Path prefixes that belong under this tab, like a student report under Class. */
  also?: string[];
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const alsoActive = also.some((prefix) => pathname.startsWith(prefix));
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cx(
          'inline-flex min-h-9 items-center whitespace-nowrap rounded-control px-3 text-sm font-medium transition-colors',
          isActive || alsoActive
            ? 'bg-brand-soft text-brand-strong'
            : 'text-ink-soft hover:bg-surface-sunken',
        )
      }
    >
      {children}
    </NavLink>
  );
}
